import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnSizingState,
  type VisibilityState,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ColumnResizeHandle } from '@/components/ui/column-resize-handle'
import { useColumnWidths } from '@/hooks/use-column-widths'
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'

interface ResizableTableProps<TData> {
  columns: ColumnDef<TData, any>[]
  data: TData[]
  storageKey: string
  defaultColumnSizing?: ColumnSizingState
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (
    updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)
  ) => void
  columnOrder?: string[]
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (field: string) => void
}

/**
 * Resizable Table Component
 *
 * A table component that supports column resizing with TanStack Table.
 * Column widths are persisted to localStorage and restored on page load.
 *
 * Features:
 * - Drag to resize columns
 * - Keyboard-accessible resize handles (Arrow keys)
 * - Column widths persist across sessions
 * - Optional server-side sorting via sortBy/sortOrder/onSort props
 * - Full WCAG 2.2 AA compliance
 *
 * @param columns - TanStack Table column definitions
 * @param data - Table data array
 * @param storageKey - Unique localStorage key for this table
 * @param defaultColumnSizing - Default column widths
 * @param sortBy - Currently sorted field (matches column meta.sortField)
 * @param sortOrder - Current sort direction
 * @param onSort - Callback when a sortable column header is clicked
 */
export function ResizableTable<TData>({
  columns,
  data,
  storageKey,
  defaultColumnSizing = {},
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  sortBy,
  sortOrder,
  onSort,
}: ResizableTableProps<TData>) {
  // Persist column widths to localStorage
  const [columnSizing, setColumnSizing] = useColumnWidths(storageKey, defaultColumnSizing)

  // Initialize TanStack Table
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    state: {
      columnSizing,
      ...(columnVisibility !== undefined && { columnVisibility }),
      ...(columnOrder && columnOrder.length > 0 && { columnOrder }),
    },
    onColumnSizingChange: setColumnSizing,
    ...(onColumnVisibilityChange && { onColumnVisibilityChange }),
    // Enable column resizing
    enableColumnResizing: true,
    // Default column sizing options
    defaultColumn: {
      minSize: 50,
      maxSize: 1000,
      size: 150,
    },
  })

  return (
    <div className="relative w-full overflow-auto">
      <Table style={{ tableLayout: 'fixed', width: table.getTotalSize() }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sortField = (header.column.columnDef.meta as any)?.sortField as
                  | string
                  | undefined
                const isSortable = !!onSort && !!sortField
                const isActive = isSortable && sortBy === sortField
                const ariaSortValue = isActive
                  ? sortOrder === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : undefined

                const handleSortClick = () => {
                  if (isSortable) onSort(sortField)
                }

                const handleSortKeyDown = (e: React.KeyboardEvent) => {
                  if (isSortable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    onSort(sortField)
                  }
                }

                return (
                  <TableHead
                    key={header.id}
                    className="relative overflow-hidden"
                    style={{
                      width: header.getSize(),
                    }}
                    aria-sort={ariaSortValue}
                  >
                    {header.isPlaceholder ? null : isSortable ? (
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex items-center gap-1 cursor-pointer select-none hover:text-foreground"
                        onClick={handleSortClick}
                        onKeyDown={handleSortKeyDown}
                        aria-label={`Sort by ${typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : header.column.id}`}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isActive ? (
                          sortOrder === 'asc' ? (
                            <ArrowUp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          )
                        ) : (
                          <ArrowUpDown
                            className="h-3.5 w-3.5 shrink-0 opacity-40"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}

                    {/* Resize handle - only on columns that can be resized */}
                    {header.column.getCanResize() && (
                      <ColumnResizeHandle
                        header={header}
                        columnName={
                          typeof header.column.columnDef.header === 'string'
                            ? header.column.columnDef.header
                            : header.column.id
                        }
                      />
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className="overflow-hidden text-ellipsis"
                    style={{
                      width: cell.column.getSize(),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
